<template>
    <div class="flex flex-row gap-1 items-center h-6">
        <a
            :href="`/analytics/franchise-hub/${props.modelValue.category}/${
                props.modelValue.metric
            }?${stringifyQueryString(props.fetchParams)}`"
            class="-ml-2 w-60 truncate rounded-sm px-2 py-0.5 text-xs font-normal transition"
            :class="{
                'text-slate-300': !totalGroupsNumber && !targetPercent,
                'cursor-pointer text-slate-700 hover:bg-sky-100 hover:text-slate-900':
                    totalGroupsNumber > 0 || targetPercent > 0,
            }"
        >
            {{ metricTitle }}
        </a>

        <div v-if="totalGroupsNumber > 0" class="flex flex-row w-full h-full">
            <vx-popover
                hover
                arrow
                :openDelay="75"
                placement="top"
                class="[&>div:first-child]:w-full [&>div:first-child]:h-full [&>div:first-child]:flex"
                :class="{
                    'min-w-2': !belowTargetPercent,
                    'min-w-6': belowTargetPercent > 0,
                }"
                :style="{ width: `${belowTargetPercent}%` }"
            >
                <metric-chunk
                    @click="props.modelValue.num_below_target > 0 && onChunkClicked({ above_target: false })"
                    :hover="props.modelValue.num_below_target > 0"
                    health="bad"
                    :size="belowTargetPercent"
                    :aria-label="chunkLabel(props.modelValue.num_below_target, false)"
                    data-test="chunk-below-target"
                />
                <template v-slot:content>
                    <span
                        class="flex flex-col items-center justify-center text-center text-sm font-normal text-slate-800"
                    >
                        <span>Groups</span>
                        <span class="font-bold">{{ props.modelValue.num_below_target }}</span>
                    </span>
                </template>
            </vx-popover>

            <vx-popover
                hover
                arrow
                :openDelay="75"
                placement="top"
                class="relative hidden z-10 flex! h-full [&>.vx-popover]:transition-opacity [&>.vx-popover]:duration-75"
            >
                <div class="group flex h-full items-center px-1">
                    <div class="h-4 w-0.5 bg-slate-200 transition group-hover:bg-slate-500"></div>
                </div>
                <template v-slot:content>
                    <span
                        class="flex flex-row items-center justify-center text-center text-sm font-normal text-slate-800"
                    >
                        <span>Goal&nbsp;</span>
                        <span class="font-bold">{{ targetPercent }}%</span>
                    </span>
                </template>
            </vx-popover>

            <vx-popover
                hover
                arrow
                :openDelay="75"
                placement="top"
                class="[&>div:first-child]:w-full [&>div:first-child]:h-full [&>div:first-child]:flex"
                :class="{
                    'min-w-2': !aboveTargetPercent,
                    'min-w-6': aboveTargetPercent > 0,
                }"
                :style="{ width: `${aboveTargetPercent}%` }"
            >
                <metric-chunk
                    @click="props.modelValue.num_above_target > 0 && onChunkClicked({ above_target: true })"
                    :hover="props.modelValue.num_above_target > 0"
                    health="good"
                    :size="aboveTargetPercent"
                    :aria-label="chunkLabel(props.modelValue.num_above_target, true)"
                    data-test="chunk-above-target"
                />
                <template v-slot:content>
                    <span
                        class="flex flex-col items-center justify-center text-center text-sm font-normal text-slate-800"
                    >
                        <span>Groups</span>
                        <span class="font-bold">{{ props.modelValue.num_above_target }}</span>
                    </span>
                </template>
            </vx-popover>
        </div>

        <div v-else class="flex flex-row w-full h-full">
            <metric-chunk health="bad" :size="100" :disabled="true"></metric-chunk>

            <vx-popover
                v-if="targetPercent > 0"
                hover
                arrow
                :openDelay="75"
                placement="top"
                class="relative z-10 flex! h-full [&>.vx-popover]:transition-opacity [&>.vx-popover]:duration-75"
            >
                <div class="group flex h-full items-center px-1">
                    <div class="h-4 w-0.5 bg-slate-200 transition group-hover:bg-slate-500"></div>
                </div>
                <template v-slot:content>
                    <span
                        class="flex flex-row items-center justify-center text-center text-sm font-normal text-slate-800"
                    >
                        <span>Goal&nbsp;</span>
                        <span class="font-bold">{{ targetPercent }}%</span>
                    </span>
                </template>
            </vx-popover>

            <div v-else class="flex h-full items-center px-1">
                <div class="h-4 w-0.5 bg-slate-200 transition"></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { VxPopover } from '@voxie/frontend-components';
import { computed } from 'vue';
import MetricChunk from '~/components/analytics/partial/MetricChunk.vue';
import { pluralized } from '~/components/filters';
import { metricAbbreviation, metricsInputValidator } from '~/utils/analytics';
import { stringifyQueryString } from '~/utils/string';
import { sendFormRequest } from '~/utils/request';

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
});

const totalGroupsNumber = computed(() => props.modelValue.num_below_target + props.modelValue.num_above_target);
const belowTargetPercent = computed(() => (props.modelValue.num_below_target / totalGroupsNumber.value) * 100);
const aboveTargetPercent = computed(() => (props.modelValue.num_above_target / totalGroupsNumber.value) * 100);
const targetPercent = computed(() => props.modelValue.target * 100);

const metricTitle = computed(() => metricAbbreviation(props.modelValue.metric));

const onChunkClicked = (data) => {
    const url = new URL(
        `analytics/franchise-hub/${props.modelValue.category}/${props.modelValue.metric}/groups`,
        window.location.origin
    );
    url.search = stringifyQueryString(props.fetchParams);

    sendFormRequest(url.toString(), { ...data, target: props.modelValue.target });
};

const chunkLabel = (count, meetsGoal) =>
    count > 0 ? `${count} ${pluralized('group', count)} ${meetsGoal ? 'meeting' : 'not meeting'} the goal` : undefined;
</script>
